/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Currencies Page - SuperAdmin Portal
 * ============================================================================
 *
 * Manage platform currencies: view, add, and toggle active status.
 *
 * ============================================================================
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Coins, Plus, ToggleLeft, ToggleRight, RefreshCw,
  CheckCircle, XCircle, X, Edit2, DollarSign, TrendingUp, Trash2
} from 'lucide-react';
import { useAllCurrencies, useCreateCurrency, useToggleCurrencyActive, useUpdateExchangeRate, useDeleteCurrency } from '@/hooks/api/useCurrencies';
import { useNotification } from '@/hooks';
import { SearchInput } from '@/components/ui';

// Types
interface CreateCurrencyForm {
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  exchangeRateToUsd: number;
}

interface EditExchangeRateForm {
  id: string;
  code: string;
  exchangeRateToUsd: number;
}

// Modal Component
const CreateCurrencyModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreateCurrencyForm) => void;
  isCreating: boolean;
}> = ({ isOpen, onClose, onCreate, isCreating }) => {
  const { t } = useTranslation('platform');
  const [form, setForm] = useState<CreateCurrencyForm>({
    code: '',
    name: '',
    symbol: '',
    decimalPlaces: 2,
    exchangeRateToUsd: 1,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      ...form,
      code: form.code.toUpperCase(),
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'decimalPlaces' ? parseInt(value, 10) :
              name === 'exchangeRateToUsd' ? parseFloat(value) || 1 : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            <Plus className="w-5 h-5" />
            {t('currencies.createModal.title', 'Add Currency')}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">{t('currencies.fields.code')}</label>
                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="USD"
                  maxLength={3}
                  required
                  className="form-input"
                  style={{ textTransform: 'uppercase' }}
                />
                <span className="form-hint">{t('currencies.hints.code')}</span>
              </div>

              <div className="form-group">
                <label className="form-label">{t('currencies.fields.symbol')}</label>
                <input
                  type="text"
                  name="symbol"
                  value={form.symbol}
                  onChange={handleChange}
                  placeholder="$"
                  maxLength={10}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('currencies.fields.name')}</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="US Dollar"
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('currencies.fields.decimalPlaces')}</label>
              <select
                name="decimalPlaces"
                value={form.decimalPlaces}
                onChange={handleChange}
                className="form-select"
              >
                <option value={0}>0 ({t('currencies.hints.noDecimals', 'no decimals')})</option>
                <option value={2}>2 ({t('currencies.hints.standard', 'standard')})</option>
                <option value={3}>3</option>
              </select>
              <span className="form-hint">{t('currencies.hints.decimalPlaces', 'Number of decimal places for display')}</span>
            </div>

            <div className="form-group">
              <label className="form-label">{t('currencies.fields.exchangeRate', 'Exchange Rate to USD')}</label>
              <input
                type="number"
                name="exchangeRateToUsd"
                value={form.exchangeRateToUsd}
                onChange={handleChange}
                placeholder="1.0"
                min="0.000001"
                step="0.01"
                required
                className="form-input"
              />
              <span className="form-hint">{t('currencies.hints.exchangeRate', 'How many units of this currency equal 1 USD')}</span>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={isCreating}>
              {isCreating ? (
                <>
                  <span className="btn-spinner" />
                  {t('currencies.createModal.creating')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t('currencies.createModal.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Exchange Rate Modal Component
const EditExchangeRateModal: React.FC<{
  isOpen: boolean;
  currency: EditExchangeRateForm | null;
  onClose: () => void;
  onUpdate: (id: string, rate: number) => void;
  isUpdating: boolean;
}> = ({ isOpen, currency, onClose, onUpdate, isUpdating }) => {
  const { t } = useTranslation('platform');
  const [rate, setRate] = useState(currency?.exchangeRateToUsd || 1);

  React.useEffect(() => {
    if (currency) {
      setRate(currency.exchangeRateToUsd);
    }
  }, [currency]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currency) {
      onUpdate(currency.id, rate);
    }
  };

  if (!isOpen || !currency) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            <TrendingUp className="w-5 h-5" />
            {t('currencies.editRate.title', 'Edit Exchange Rate')}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="rate-currency-info">
              <span className="rate-currency-code">{currency.code}</span>
              <span className="rate-arrow">→</span>
              <span className="rate-usd">USD</span>
            </div>

            <div className="form-group">
              <label className="form-label">{t('currencies.fields.exchangeRate', 'Exchange Rate to USD')}</label>
              <div className="rate-input-wrapper">
                <span className="rate-prefix">1 USD =</span>
                <input
                  type="number"
                  value={rate}
                  onChange={(e) => setRate(parseFloat(e.target.value) || 0)}
                  placeholder="1.0"
                  min="0.000001"
                  step="0.01"
                  required
                  className="form-input rate-input"
                  autoFocus
                />
                <span className="rate-suffix">{currency.code}</span>
              </div>
              <span className="form-hint">
                {t('currencies.hints.exchangeRateExample', 'Example: For GNF, enter 8600 (1 USD = 8,600 GNF)')}
              </span>
            </div>

            <div className="rate-preview">
              <div className="rate-preview-item">
                <span className="preview-label">$50 USD =</span>
                <span className="preview-value">{currency.code} {(50 * rate).toLocaleString()}</span>
              </div>
              <div className="rate-preview-item">
                <span className="preview-label">$200 USD =</span>
                <span className="preview-value">{currency.code} {(200 * rate).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={isUpdating || rate <= 0}>
              {isUpdating ? (
                <>
                  <span className="btn-spinner" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4" />
                  {t('currencies.editRate.save', 'Update Rate')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Page
export default function CurrenciesPage() {
  const { t } = useTranslation('platform');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [editingCurrency, setEditingCurrency] = useState<EditExchangeRateForm | null>(null);

  const [deletingCurrency, setDeletingCurrency] = useState<{ id: string; code: string } | null>(null);

  const { data: currencies = [], isLoading, refetch } = useAllCurrencies();
  const createMutation = useCreateCurrency();
  const toggleMutation = useToggleCurrencyActive();
  const updateRateMutation = useUpdateExchangeRate();
  const deleteMutation = useDeleteCurrency();
  const { success, error: showError } = useNotification();

  // Filter currencies
  const filteredCurrencies = useMemo(() => {
    return currencies.filter(c => {
      const matchesSearch = searchQuery === '' ||
        c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && c.isActive) ||
        (statusFilter === 'INACTIVE' && !c.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [currencies, searchQuery, statusFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: currencies.length,
    active: currencies.filter(c => c.isActive).length,
    inactive: currencies.filter(c => !c.isActive).length,
  }), [currencies]);

  const handleCreate = (data: CreateCurrencyForm) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        success(t('currencies.messages.createSuccess', { code: data.code }));
        setShowCreateModal(false);
      },
      onError: (err) => {
        showError(err instanceof Error ? err.message : t('currencies.messages.createError'));
      },
    });
  };

  const handleToggle = (id: string, code: string, currentStatus: boolean) => {
    toggleMutation.mutate(id, {
      onSuccess: () => {
        success(t(currentStatus ? 'currencies.messages.deactivated' : 'currencies.messages.activated', { code }));
      },
      onError: (err) => {
        showError(err instanceof Error ? err.message : t('currencies.messages.toggleError'));
      },
    });
  };

  const handleUpdateRate = (id: string, exchangeRateToUsd: number) => {
    updateRateMutation.mutate({ id, exchangeRateToUsd }, {
      onSuccess: () => {
        success(t('currencies.messages.rateUpdated', 'Exchange rate updated successfully'));
        setEditingCurrency(null);
      },
      onError: (err) => {
        showError(err instanceof Error ? err.message : t('currencies.messages.rateUpdateError', 'Failed to update exchange rate'));
      },
    });
  };

  const openEditRateModal = (currency: { id: string; code: string; exchangeRateToUsd: number }) => {
    setEditingCurrency({
      id: currency.id,
      code: currency.code,
      exchangeRateToUsd: currency.exchangeRateToUsd,
    });
  };

  const handleDelete = (id: string, code: string) => {
    setDeletingCurrency({ id, code });
  };

  const confirmDelete = () => {
    if (!deletingCurrency) return;
    deleteMutation.mutate(deletingCurrency.id, {
      onSuccess: () => {
        success(t('currencies.messages.deleted', { code: deletingCurrency.code }) || `Currency ${deletingCurrency.code} deleted`);
        setDeletingCurrency(null);
      },
      onError: (err) => {
        showError(err instanceof Error ? err.message : t('currencies.messages.deleteError', 'Cannot delete currency - it may be in use'));
      },
    });
  };

  if (isLoading) {
    return (
      <div className="currencies-loading">
        <div className="spinner" />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="currencies-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <Coins className="w-6 h-6" />
            {t('currencies.title')}
          </h1>
          <p className="page-subtitle">{t('currencies.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4" />
          {t('currencies.addCurrency')}
        </button>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">{t('currencies.stats.total')}</span>
        </div>
        <div className="stat-card stat-success">
          <span className="stat-value">{stats.active}</span>
          <span className="stat-label">{t('currencies.stats.active')}</span>
        </div>
        <div className="stat-card stat-muted">
          <span className="stat-value">{stats.inactive}</span>
          <span className="stat-label">{t('currencies.stats.inactive')}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('currencies.searchPlaceholder')}
          className="search-box"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'ACTIVE' | 'INACTIVE')}
          className="filter-select"
        >
          <option value="ALL">{t('currencies.filter.all')}</option>
          <option value="ACTIVE">{t('currencies.filter.active')}</option>
          <option value="INACTIVE">{t('currencies.filter.inactive')}</option>
        </select>
        <button className="btn-icon" onClick={() => refetch()} title={t('common.refresh')}>
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Currency List */}
      <div className="currencies-grid">
        {filteredCurrencies.length === 0 ? (
          <div className="empty-state">
            <Coins className="w-12 h-12" />
            <p>{t('currencies.empty')}</p>
          </div>
        ) : (
          filteredCurrencies.map(currency => (
            <div key={currency.id} className={`currency-card ${!currency.isActive ? 'currency-inactive' : ''}`}>
              <div className="currency-header">
                <div className="currency-symbol-box">
                  {currency.symbol}
                </div>
                <div className="currency-info">
                  <span className="currency-code">{currency.code}</span>
                  <span className="currency-name">{currency.name}</span>
                </div>
                <div className={`currency-status ${currency.isActive ? 'status-active' : 'status-inactive'}`}>
                  {currency.isActive ? (
                    <><CheckCircle className="w-3 h-3" /> {t('currencies.status.active')}</>
                  ) : (
                    <><XCircle className="w-3 h-3" /> {t('currencies.status.inactive')}</>
                  )}
                </div>
              </div>

              <div className="currency-details">
                <div className="detail-item">
                  <span className="detail-label">{t('currencies.fields.decimalPlaces')}</span>
                  <span className="detail-value">{currency.decimalPlaces}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('currencies.fields.format')}</span>
                  <span className="detail-value font-mono">
                    {currency.symbol}{(1234.56).toFixed(currency.decimalPlaces)}
                  </span>
                </div>
              </div>

              <div className="currency-rate-section">
                <div className="rate-display">
                  <span className="rate-label">{t('currencies.fields.exchangeRate', 'Exchange Rate')}</span>
                  <span className="rate-value">
                    1 USD = <strong>{currency.exchangeRateToUsd?.toLocaleString() || '1'}</strong> {currency.code}
                  </span>
                </div>
                <button
                  className="edit-rate-btn"
                  onClick={() => openEditRateModal(currency)}
                  title={t('currencies.actions.editRate', 'Edit exchange rate')}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>

              <div className="currency-actions">
                <button
                  className={`toggle-btn ${currency.isActive ? 'toggle-active' : 'toggle-inactive'}`}
                  onClick={() => handleToggle(currency.id, currency.code, currency.isActive)}
                  disabled={toggleMutation.isPending}
                >
                  {currency.isActive ? (
                    <><ToggleRight className="w-4 h-4" /> {t('currencies.actions.deactivate')}</>
                  ) : (
                    <><ToggleLeft className="w-4 h-4" /> {t('currencies.actions.activate')}</>
                  )}
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(currency.id, currency.code)}
                  title={t('currencies.actions.delete', 'Delete currency')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Results count */}
      <div className="results-count">
        {t('currencies.showing', { count: filteredCurrencies.length, total: currencies.length })}
      </div>

      {/* Create Modal */}
      <CreateCurrencyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        isCreating={createMutation.isPending}
      />

      {/* Edit Exchange Rate Modal */}
      <EditExchangeRateModal
        isOpen={!!editingCurrency}
        currency={editingCurrency}
        onClose={() => setEditingCurrency(null)}
        onUpdate={handleUpdateRate}
        isUpdating={updateRateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      {deletingCurrency && (
        <div className="modal-overlay" onClick={() => setDeletingCurrency(null)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header delete-modal-header">
              <h2 className="modal-title">
                <Trash2 className="w-5 h-5" />
                {t('currencies.deleteModal.title', 'Delete Currency')}
              </h2>
              <button className="modal-close" onClick={() => setDeletingCurrency(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <p className="delete-warning">
                {t('currencies.deleteModal.warning', 'Are you sure you want to delete')} <strong>{deletingCurrency.code}</strong>?
              </p>
              <p className="delete-note">
                {t('currencies.deleteModal.note', 'This action cannot be undone. The currency cannot be deleted if it is in use by any organization.')}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeletingCurrency(null)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <span className="btn-spinner" />
                    {t('common.deleting', 'Deleting...')}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    {t('common.delete', 'Delete')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{currenciesStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const currenciesStyles = `
  .currencies-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Loading */
  .currencies-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 64px;
    color: var(--color-text-muted);
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-border-subtle);
    border-top-color: var(--color-brand-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin-top: 4px;
  }

  /* Stats Row */
  .stats-row {
    display: flex;
    gap: 16px;
  }

  .stat-card {
    flex: 1;
    padding: 16px 20px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    text-align: center;
  }

  .stat-value {
    display: block;
    font-size: 28px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .stat-label {
    display: block;
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }

  .stat-success .stat-value {
    color: #10B981;
  }

  .stat-muted .stat-value {
    color: var(--color-text-muted);
  }

  /* Filters */
  .filters-row {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .search-box {
    flex: 1;
    max-width: 300px;
  }

  .filter-select {
    padding: 10px 14px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    font-size: 14px;
    color: var(--color-text-primary);
    outline: none;
  }

  .btn-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-icon:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  /* Currency Grid */
  .currencies-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
  }

  .currency-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 20px;
    transition: all 0.15s ease;
  }

  .currency-card:hover {
    border-color: var(--color-border-default);
  }

  .currency-inactive {
    opacity: 0.6;
  }

  .currency-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .currency-symbol-box {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-brand-primary);
    border-radius: 10px;
    font-size: 18px;
    font-weight: 700;
    color: white;
  }

  .currency-info {
    flex: 1;
  }

  .currency-code {
    display: block;
    font-size: 16px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .currency-name {
    display: block;
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .currency-status {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .status-active {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
  }

  .status-inactive {
    background: var(--color-bg-primary);
    color: var(--color-text-muted);
  }

  .currency-details {
    display: flex;
    gap: 24px;
    padding: 12px 0;
    border-top: 1px solid var(--color-border-subtle);
    border-bottom: 1px solid var(--color-border-subtle);
    margin-bottom: 16px;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .detail-label {
    font-size: 10px;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }

  .detail-value {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .currency-actions {
    display: flex;
    justify-content: flex-end;
  }

  .toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .toggle-active {
    background: rgba(239, 68, 68, 0.1);
    color: #EF4444;
  }

  .toggle-active:hover {
    background: rgba(239, 68, 68, 0.2);
  }

  .toggle-inactive {
    background: rgba(16, 185, 129, 0.1);
    color: #10B981;
  }

  .toggle-inactive:hover {
    background: rgba(16, 185, 129, 0.2);
  }

  .toggle-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Empty State */
  .empty-state {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px;
    color: var(--color-text-muted);
    text-align: center;
  }

  .empty-state p {
    margin-top: 12px;
    font-size: 14px;
  }

  /* Results Count */
  .results-count {
    font-size: 12px;
    color: var(--color-text-muted);
    text-align: center;
  }

  /* Button */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    height: 44px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-brand-primary-hover);
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text-primary);
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  .btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .modal-content {
    width: 100%;
    max-width: 480px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 16px;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .modal-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .modal-close {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .modal-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .modal-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 20px;
    border-top: 1px solid var(--color-border-subtle);
    background: var(--color-bg-primary);
  }

  /* Form */
  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  .form-input,
  .form-select {
    padding: 10px 14px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    font-size: 14px;
    color: var(--color-text-primary);
    outline: none;
  }

  .form-input:focus,
  .form-select:focus {
    border-color: var(--color-brand-primary);
  }

  .form-hint {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .font-mono {
    font-family: var(--font-mono);
  }

  /* Currency Rate Section */
  .currency-rate-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    background: var(--color-bg-primary);
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .rate-display {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .rate-label {
    font-size: 10px;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }

  .rate-value {
    font-size: 14px;
    color: var(--color-text-primary);
    font-family: var(--font-mono);
  }

  .rate-value strong {
    color: var(--color-brand-primary);
  }

  .edit-rate-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-brand-primary);
    border: none;
    border-radius: 8px;
    color: white;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .edit-rate-btn:hover {
    background: var(--color-brand-primary-hover);
    transform: scale(1.05);
  }

  /* Edit Rate Modal */
  .modal-sm {
    max-width: 400px;
  }

  .rate-currency-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px;
    background: var(--color-bg-primary);
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .rate-currency-code {
    font-size: 24px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-brand-primary);
  }

  .rate-arrow {
    font-size: 20px;
    color: var(--color-text-muted);
  }

  .rate-usd {
    font-size: 24px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .rate-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .rate-prefix {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-secondary);
    white-space: nowrap;
  }

  .rate-input {
    flex: 1;
    text-align: right;
    font-family: var(--font-mono);
    font-size: 16px;
  }

  .rate-suffix {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    font-family: var(--font-mono);
  }

  .rate-preview {
    margin-top: 16px;
    padding: 12px;
    background: var(--color-bg-primary);
    border-radius: 8px;
    border: 1px dashed var(--color-border-subtle);
  }

  .rate-preview-item {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 13px;
  }

  .preview-label {
    color: var(--color-text-muted);
  }

  .preview-value {
    font-family: var(--font-mono);
    font-weight: 600;
    color: var(--color-text-primary);
  }

  /* Delete Button */
  .delete-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .delete-btn:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: #EF4444;
    color: #EF4444;
  }

  .currency-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  /* Delete Modal */
  .delete-modal-header {
    border-bottom-color: rgba(239, 68, 68, 0.3);
  }

  .delete-modal-header .modal-title {
    color: #EF4444;
  }

  .delete-warning {
    font-size: 15px;
    color: var(--color-text-primary);
    margin-bottom: 12px;
  }

  .delete-note {
    font-size: 13px;
    color: var(--color-text-muted);
    background: var(--color-bg-primary);
    padding: 12px;
    border-radius: 8px;
    border-left: 3px solid #F59E0B;
  }

  .btn-danger {
    background: #EF4444;
    color: white;
  }

  .btn-danger:hover:not(:disabled) {
    background: #DC2626;
  }

  .btn-danger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .btn {
      width: 100%;
      justify-content: center;
    }

    .stats-row {
      flex-direction: column;
    }

    .filters-row {
      flex-wrap: wrap;
    }

    .search-box {
      max-width: 100%;
      order: 3;
      width: 100%;
    }

    .currencies-grid {
      grid-template-columns: 1fr;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }
  }
`;
